import {
  handleCreateResponse,
  useCreateSmsChannelsMutation,
  useGetSmsChannelsQuery,
} from "@/react/features/vcc/sms-channels/smsChannelsApi";
import { useSelector } from "react-redux";
import { tenantSelector } from "@/react/features/customer/customerSlice";
import TableComponent from "@/react/components/table/TableComponent";
import schema from "@/react/features/vcc/sms-channels/schema";
import React, { useCallback } from "react";
import { errorNotification, useErrorNotification } from "@/react/notifications";
import columnDefinition from "@/react/features/vcc/sms-channels/columnDefinition";
import { useTable } from "@/react/components/table/useTable";
import mergeNewRowIntoSelection from "@/react/components/table/mergeNewRowIntoSelection";
import TableWrapper from "@/react/features/styled/TableWrapper";
import ContentWrapper from "@/react/features/styled/PageContentContainer";
import PageTitle from "@/react/components/PageTitle";

const columns = Object.freeze(columnDefinition());

export function SmsChannelsPage() {
  const tenant = useSelector(tenantSelector);
  const tenantName = tenant?.tenantId;
  const {
    data: initialData,
    error,
    isFetching,
    refetch,
  } = useGetSmsChannelsQuery(tenantName);

  useErrorNotification(error);

  const [createSmsChannels, { isLoading: isCreating }] =
    useCreateSmsChannelsMutation();

  const idKey = schema.fields.channelId.spec.key;

  const { table, filters, state, mutations } = useTable(
    initialData,
    columns,
    schema,
    idKey,
    {
      enableRowSelection: (row) => {
        return !row.original[idKey];
      },
    }
  );

  const { setRowSelection } = state;
  const {
    setData,
    rowsToCreate,
    setRowsToCreate,
    addRows,
    updateRows,
    deleteRows,
    hasErrors,
  } = mutations;

  const isDirty = rowsToCreate.size > 0;

  const onSave = () => {
    if (rowsToCreate.size === 0) return;
    const requestList = Array.from(rowsToCreate.values());
    createSmsChannels({ tenantName, requestList })
      .unwrap()
      .then(
        handleCreateResponse({
          rowsToCreate,
          setRowsToCreate,
          setData,
        })
      )
      .then(() => {
        setRowSelection(
          Array.from(rowsToCreate.keys()).reduce((acc, key) => {
            acc[key] = true;
            return acc;
          })
        );
      })
      .catch(errorNotification);
  };

  const isTableLoading = isFetching || isCreating;

  const overrideMutations = {
    hasErrors,
    updateRows,
    deleteRows,
    addRows: useCallback(
      mergeNewRowIntoSelection({ addRows, setRowSelection }),
      [addRows, setRowSelection]
    ),
  };

  return (
    <ContentWrapper>
      <PageTitle>SMS Channels</PageTitle>
      <TableWrapper>
        <TableComponent
          title="SMS Channels"
          schema={schema}
          isDirty={isDirty}
          isLoading={isTableLoading}
          onSave={onSave}
          onCancel={mutations.resetData}
          onRefresh={refetch}
          table={table}
          filters={filters}
          mutations={overrideMutations}
          state={state}
        />
      </TableWrapper>
    </ContentWrapper>
  );
}
